// ============================================================
// RIZKA TRAVEL CRM — TOP-BAR TYPEAHEAD SEARCH
// File: src/components/TopBarSearch.js
// ============================================================
// Live search across bookings + customers. Sits in the top nav bar.
// Behavior:
//   - 2+ chars triggers (debounced 250 ms)
//   - Results grouped: Bookings / Customers
//   - Click → navigate to detail page
//   - Esc or outside-click → close
//   - Arrow keys + Enter → keyboard navigation
//   - Cmd/Ctrl + K → focus the input from anywhere

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';

// ── Status badge colours (mirrors BookingHeader) ──────────────
const STATUS_COLOURS = {
    pending:    { bg: '#fef3c7', fg: '#92400e' },
    confirmed:  { bg: '#d1fae5', fg: '#065f46' },
    ready:      { bg: '#dbeafe', fg: '#1e40af' },
    dispatched: { bg: '#e0e7ff', fg: '#3730a3' },
    closed:     { bg: '#f3f4f6', fg: '#374151' },
    cancelled:  { bg: '#fee2e2', fg: '#991b1b' },
};

function StatusBadge({ status }) {
    const c = STATUS_COLOURS[status] || STATUS_COLOURS.pending;
    return (
        <span style={{
            background: c.bg, color: c.fg,
            fontSize: '0.6875rem', fontWeight: 600,
            padding: '0.125rem 0.5rem', borderRadius: '9999px',
            textTransform: 'capitalize', whiteSpace: 'nowrap'
        }}>
            {status || 'pending'}
        </span>
    );
}

function fmtDate(d) {
    if (!d) return '';
    try {
        return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch { return ''; }
}

function customerDisplayName(c) {
    if (c.company_name) return c.company_name;
    return [c.title, c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.email || 'Unnamed';
}

export default function TopBarSearch() {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const wrapRef = useRef(null);
    const debounceRef = useRef(null);

    const [query, setQuery] = useState('');
    const [results, setResults] = useState({ bookings: [], customers: [] });
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Flatten results into a navigable list for keyboard nav
    const flatItems = [
        ...results.bookings.map(b => ({ type: 'booking', data: b })),
        ...results.customers.map(c => ({ type: 'customer', data: c })),
    ];

    // ── Debounced search ─────────────────────────────────────
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        const q = query.trim();
        if (q.length < 2) {
            setResults({ bookings: [], customers: [] });
            setIsLoading(false);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setIsLoading(true);
            try {
                const res = await api.get(`/search/all?q=${encodeURIComponent(q)}&limit=8`);
                setResults({
                    bookings: res.data.bookings || [],
                    customers: res.data.customers || [],
                });
                setSelectedIndex(0);
            } catch (err) {
                console.error('Search failed:', err);
                setResults({ bookings: [], customers: [] });
            } finally {
                setIsLoading(false);
            }
        }, 250);

        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [query]);

    // ── Outside-click & Escape to close ──────────────────────
    useEffect(() => {
        function onDocClick(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        }
        function onKey(e) {
            if (e.key === 'Escape') {
                setIsOpen(false);
                inputRef.current?.blur();
            }
        }
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, []);

    // ── Cmd/Ctrl + K focus shortcut ──────────────────────────
    useEffect(() => {
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                inputRef.current?.focus();
                inputRef.current?.select();
            }
        }
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    // ── Navigate to a result ─────────────────────────────────
    const goTo = useCallback((item) => {
        if (item.type === 'booking') {
            navigate(`/bookings/${item.data.id}`);
        } else {
            navigate(`/customers/${item.data.id}`);
        }
        setIsOpen(false);
        setQuery('');
    }, [navigate]);

    // ── Keyboard nav within results ──────────────────────────
    function onInputKeyDown(e) {
        if (!isOpen) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(i => Math.min(i + 1, flatItems.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && flatItems[selectedIndex]) {
            e.preventDefault();
            goTo(flatItems[selectedIndex]);
        }
    }

    const showDropdown = isOpen && query.trim().length >= 2;
    const hasResults = flatItems.length > 0;

    // ── Style atoms ──────────────────────────────────────────
    const inputStyle = {
        width: '100%',
        height: '36px',
        padding: '0 0.75rem 0 2.25rem',
        fontSize: '0.875rem',
        background: 'white',
        border: `1.5px solid ${isOpen ? '#9fb3c8' : '#d9e2ec'}`,
        borderRadius: '8px',
        outline: 'none',
        color: '#102a43',
        transition: 'border-color 0.15s',
    };

    const dropdownStyle = {
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0, right: 0,
        background: 'white',
        border: '1px solid #d9e2ec',
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(16, 42, 67, 0.12)',
        maxHeight: '520px',
        overflowY: 'auto',
        zIndex: 100,
    };

    const groupHeaderStyle = {
        padding: '0.5rem 0.875rem 0.375rem',
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#627d98',
        background: '#f7fafc',
        borderBottom: '1px solid #edf2f7',
    };

    return (
        <div ref={wrapRef} style={{ position: 'relative', width: '100%', maxWidth: '480px' }}>
            <div style={{ position: 'relative' }}>
                {/* Search icon */}
                <div style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#829ab1',
                    fontSize: '0.875rem',
                    pointerEvents: 'none',
                }}>🔍</div>

                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={e => { setQuery(e.target.value); setIsOpen(true); }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={onInputKeyDown}
                    placeholder="Search bookings, customers…  (Cmd/Ctrl + K)"
                    style={inputStyle}
                />

                {/* Loading spinner / clear button */}
                {query && (
                    <button
                        onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                        style={{
                            position: 'absolute',
                            right: '0.5rem', top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none', border: 'none',
                            color: '#829ab1', cursor: 'pointer',
                            fontSize: '1rem', padding: '0.25rem',
                            lineHeight: 1,
                        }}
                        title="Clear"
                    >✕</button>
                )}
            </div>

            {/* Dropdown */}
            {showDropdown && (
                <div style={dropdownStyle}>
                    {isLoading && (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#627d98', fontSize: '0.8125rem' }}>
                            Searching…
                        </div>
                    )}

                    {!isLoading && !hasResults && (
                        <div style={{ padding: '1.25rem', textAlign: 'center', color: '#829ab1', fontSize: '0.8125rem' }}>
                            No matches for <strong style={{ color: '#334e68' }}>{query}</strong>
                        </div>
                    )}

                    {!isLoading && results.bookings.length > 0 && (
                        <>
                            <div style={groupHeaderStyle}>Bookings · {results.bookings.length}</div>
                            {results.bookings.map((b, i) => {
                                const idx = i;
                                const isSel = selectedIndex === idx;
                                const subtitle = [
                                    b.lead_passenger_name,
                                    b.company_name,
                                    b.destination,
                                    fmtDate(b.departure_date),
                                ].filter(Boolean).join(' · ');
                                return (
                                    <button
                                        key={b.id}
                                        onClick={() => goTo({ type: 'booking', data: b })}
                                        onMouseEnter={() => setSelectedIndex(idx)}
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '0.75rem',
                                            padding: '0.5rem 0.875rem',
                                            background: isSel ? '#f0f4f8' : 'white',
                                            border: 'none',
                                            borderBottom: '1px solid #edf2f7',
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                                color: '#102a43',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                            }}>
                                                {b.reference}
                                                {b.is_atol_protected && (
                                                    <span style={{
                                                        fontSize: '0.625rem',
                                                        background: '#dbeafe', color: '#1e40af',
                                                        padding: '0.0625rem 0.375rem',
                                                        borderRadius: '4px',
                                                        fontWeight: 700,
                                                    }}>ATOL</span>
                                                )}
                                            </div>
                                            {subtitle && (
                                                <div style={{
                                                    fontSize: '0.75rem',
                                                    color: '#627d98',
                                                    marginTop: '0.125rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {subtitle}
                                                </div>
                                            )}
                                        </div>
                                        <StatusBadge status={b.status} />
                                    </button>
                                );
                            })}
                        </>
                    )}

                    {!isLoading && results.customers.length > 0 && (
                        <>
                            <div style={groupHeaderStyle}>Customers · {results.customers.length}</div>
                            {results.customers.map((c, i) => {
                                const idx = results.bookings.length + i;
                                const isSel = selectedIndex === idx;
                                const name = customerDisplayName(c);
                                const sub = [c.email, c.customer_type].filter(Boolean).join(' · ');
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => goTo({ type: 'customer', data: c })}
                                        onMouseEnter={() => setSelectedIndex(idx)}
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            padding: '0.5rem 0.875rem',
                                            background: isSel ? '#f0f4f8' : 'white',
                                            border: 'none',
                                            borderBottom: '1px solid #edf2f7',
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <div style={{
                                            width: '28px', height: '28px',
                                            borderRadius: '50%',
                                            background: c.company_name ? '#e0e7ff' : '#fef3c7',
                                            color: c.company_name ? '#3730a3' : '#92400e',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.75rem', fontWeight: 700,
                                            flexShrink: 0,
                                        }}>
                                            {c.company_name ? '🏢' : '👤'}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                                color: '#102a43',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {name}
                                            </div>
                                            {sub && (
                                                <div style={{
                                                    fontSize: '0.75rem',
                                                    color: '#627d98',
                                                    marginTop: '0.125rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {sub}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </>
                    )}

                    {/* Footer hint */}
                    {!isLoading && hasResults && (
                        <div style={{
                            padding: '0.375rem 0.875rem',
                            background: '#f7fafc',
                            color: '#829ab1',
                            fontSize: '0.6875rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            borderTop: '1px solid #edf2f7',
                        }}>
                            <span>↑↓ navigate · ↵ open · esc close</span>
                            <span>{flatItems.length} {flatItems.length === 1 ? 'result' : 'results'}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
